import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Text } from '@ma-react/components';

import Error from 'components/Error';
import YesNoOptions from 'components/Input/YesNoOptions';
import Label from 'components/Label';
import NonOwnerQuestions from 'components/NonOwnerQuestions';
import NotFoundPage from 'components/NotFoundPage';
import ProjectQuestions from 'components/ProjectQuestions';
import RetrievePhone from 'components/RetrievePhone';
import Throbber from 'components/Throbber';
import ThrobberButton from 'components/ThrobberButton';
import * as Device from 'helpers/Device';
import {
    apartmentFloor,
    propertyArea,
    propertyType,
    roomCount,
} from 'helpers/FormattedSpecifications';
import { isApartment } from 'helpers/FormInformations';
import { translate } from 'helpers/Translate';
import StaticMapService from 'services/StaticMapService';

/**
 * Barometre Functional Component.
 */
export default function Barometre({
    error,
    estima,
    formValid,
    goToStepsForm,
    onErrorDiscard,
    pending,
    submit,
    withPhoneRetrieval,
    isPhoneStep,
    setPhoneStep,
}) {
    let content = null;

    /**
     * Handler for Barometre button click based on phone retrieval need or not
     * Nested in the component because it is using setPhoneStep hook dispatcher
     */
    function onBarometreButtonClick() {
        if (!withPhoneRetrieval || isPhoneStep) {
            submit();
        } else {
            setPhoneStep(true);
        }
    }

    /**
     * Go back to previous step
     */
    function goToPreviousStep() {
        setPhoneStep(false);
    }

    if (estima && estima.coordinates) {
        let form = null;
        let buttons = null;

        const updateEstimaForm = () => (
            <React.Fragment>
                <h3 className="title--h4">
                    {translate(
                        'Mettez à jour vos informations et découvrez votre nouvelle estimation',
                    )}
                </h3>
                <div className="row-container row-container--padding-top">
                    <div className="row-container__label row-container__label--yes-no">
                        <Label
                            labelFor="profile_owner"
                            text={translate('Êtes-vous propriétaire de ce bien ?')}
                            small
                        />
                    </div>
                    <div className="row-container__field row-container__field--yes-no">
                        <YesNoOptions name="profile_owner" />
                    </div>
                </div>
                <Text
                    size="xs"
                    className="no-padding-sm-top padding-top padding-sm-bottom-double padding-bottom-triple"
                >
                    {translate(
                        "En fournissant ces informations, vous pourrez être mis en relation avec nos experts immobiliers ou un professionnel de l'immobilier local afin de mieux vous accompagner dans votre projet.",
                    )}
                </Text>

                {estima.profile_owner === true && <ProjectQuestions values={estima} small />}
                {estima.profile_owner === false && <NonOwnerQuestions values={estima} small />}
            </React.Fragment>
        );

        const submitButtons = ({
            text = translate("Actualiser l'estimation"),
            className = 'btn--primary',
            hasFormValidation = true,
            hasPrevious = false,
        } = {}) => {
            const previousButton = hasPrevious ? (
                <button
                    className="btn btn--secondary btn--previous btn--fluid"
                    onClick={goToPreviousStep}
                    data-previous
                    type="button"
                >
                    &larr; {translate('Retour')}
                </button>
            ) : null;

            return Device.isMobileOrTablet() ? (
                <div
                    className={classnames('estima-bottom-nav', {
                        'estima-bottom-nav--double': hasPrevious,
                    })}
                >
                    {previousButton}
                    <ThrobberButton
                        type="button"
                        onClick={onBarometreButtonClick}
                        disabled={hasFormValidation && !formValid}
                        className={classnames(
                            'btn',
                            'btn--small',
                            'barometre__update-button',
                            className,
                        )}
                        pending={pending}
                        data-submit-mobile
                    >
                        {text}
                    </ThrobberButton>
                </div>
            ) : (
                <div className="barometre__btn-form-group">
                    <div className="flex-full">{previousButton}</div>
                    <div className="flex-full">
                        <ThrobberButton
                            type="button"
                            onClick={onBarometreButtonClick}
                            disabled={hasFormValidation && !formValid}
                            className={classnames('btn', className)}
                            pending={pending}
                            data-submit-desktop
                        >
                            {text}
                        </ThrobberButton>
                    </div>
                </div>
            );
        };

        if (withPhoneRetrieval) {
            const phoneStep = () => (
                <React.Fragment>
                    <h3 className="title--h4 margin-bottom-double">
                        {translate(
                            'Dernière étape avant votre estimation : vérifiez vos informations.',
                        )}
                    </h3>
                    <RetrievePhone />
                </React.Fragment>
            );
            form = isPhoneStep ? phoneStep() : updateEstimaForm();
            buttons = isPhoneStep
                ? submitButtons({ hasFormValidation: false, hasPrevious: true })
                : submitButtons({ text: translate('Suivant'), className: 'btn--success' });
        } else {
            form = updateEstimaForm();
            buttons = submitButtons();
        }

        content = (
            <div>
                <Error error={error} discard={onErrorDiscard} />

                <div className="barometre__informations-map">
                    <div className="barometre__informations">
                        <p className="barometre__subtitle hide-sm">
                            {translate('Mise à jour de votre estimation au ')}
                        </p>
                        <h2 className="barometre__address">
                            <span className="hide-lg">
                                {translate('Mise à jour de votre estimation au ')}
                            </span>
                            {estima.address_name}, {estima.zip}&nbsp;{estima.city_name}
                        </h2>
                        <ul className="list-specifications list-specifications--barometre">
                            {estima._property_type ? (
                                <li>{propertyType(estima._property_type)}</li>
                            ) : null}
                            {estima.room_count ? (
                                <li>{roomCount(estima.room_count, estima)}</li>
                            ) : null}
                            {estima.area ? <li>{propertyArea(estima.area)}</li> : null}
                            {isApartment(estima) && estima.floor ? (
                                <li>{apartmentFloor(estima)}</li>
                            ) : null}
                            {estima.elevator && <li>{translate('Ascenseur')}</li>}
                        </ul>
                        <button
                            data-edit-characteristics-button-desktop
                            type="button"
                            className="btn btn--secondary btn--tiny hide-sm"
                            onClick={goToStepsForm}
                        >
                            {translate('Editer les caractéristiques')}
                        </button>
                        <button
                            data-edit-characteristics-button-mobile
                            type="button"
                            className="arrow-link arrow-link--barometre hide-lg"
                            onClick={goToStepsForm}
                        >
                            {translate('Editer les caractéristiques')}
                        </button>
                    </div>
                    <img
                        className="barometre__map"
                        src={StaticMapService.getStaticMapUrl(
                            estima.coordinates.lat,
                            estima.coordinates.lng,
                            216,
                            135,
                            15,
                        )}
                        alt={translate('Position du bien estimé')}
                    />
                </div>

                <div className="barometre__update">
                    {form}
                    {buttons}
                </div>
            </div>
        );
    } else if (pending) {
        content = <Throbber label={translate('Chargement de votre estimation')} />;
    } else {
        content = <NotFoundPage />;
    }

    return (
        <div className="barometre">
            <div className="panel panel--barometre">{content}</div>
        </div>
    );
}

Barometre.displayName = 'Barometre';

Barometre.propTypes = {
    estima: PropTypes.shape({}),
    goToStepsForm: PropTypes.func,
    pending: PropTypes.bool,
    submit: PropTypes.func.isRequired,
    formValid: PropTypes.bool,
    withPhoneRetrieval: PropTypes.bool,
    isPhoneStep: PropTypes.bool.isRequired,
    setPhoneStep: PropTypes.func.isRequired,
};

Barometre.defaultProps = {
    withPhoneRetrieval: false,
};
